/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { AtpStatus } from '@/lib/atp/atp-fields';

/**
 * Single source of truth for cart line-item unavailability checks across
 * product rows and checkout CTA disablement rules.
 */
export function isUnavailableCartItem(item: Record<string, unknown> | null | undefined): boolean {
    if (!item) return false;

    const inventory = item.inventory as Record<string, unknown> | null | undefined;
    const isTruthy = (value: unknown): boolean => value === true || value === 'true';
    const isFalsy = (value: unknown): boolean => value === false || value === 'false';

    return (
        isTruthy(item.showInventoryMessage) ||
        isTruthy(item.isProductUnavailable) ||
        isFalsy(item.orderable) ||
        isFalsy(inventory?.orderable)
    );
}

/**
 * Hard unavailability signals only (no message-only/soft flags).
 * Use this for catalog-level fallbacks where low-stock messaging should not
 * block checkout.
 */
export function isHardUnavailableCartItem(item: Record<string, unknown> | null | undefined): boolean {
    if (!item) return false;
    const inventory = item.inventory as Record<string, unknown> | null | undefined;
    const isFalsy = (value: unknown): boolean => value === false || value === 'false';
    const isTruthy = (value: unknown): boolean => value === true || value === 'true';
    return isTruthy(item.isProductUnavailable) || isFalsy(item.orderable) || isFalsy(inventory?.orderable);
}

/**
 * Checkout-blocking variant of unavailable checks.
 *
 * ATP blocks checkout on a **PLI-flag-first** rule: `c_atpIsAvailable === false`
 * on the response line item is authoritative on its own. That field only
 * transitions to `false` when a successful ATP call for the current
 * `(zip, cart)` state persisted the unavailable verdict onto
 * `pli.custom.atpIsAvailable` (SCAPI then projects it back onto the response
 * as `c_atpIsAvailable`). It cannot be produced by a service failure
 * (`c_atpStatus === 'error'`), a missing ZIP (`no_zip`), or a mutation-window
 * skip (`deferred`) — those paths leave the field un-touched and its prior
 * value stands.
 *
 * Consequences of the PLI-flag-first rule:
 *   1. The block fires the instant the ATP verdict lands on the PLI, even
 *      when the current basket-GET returned `c_atpStatus === 'deferred'`
 *      (mutation windows now block correctly instead of dropping the gate).
 *   2. `basketAtpStatus === 'enriched'` remains supported as an **additive**
 *      trigger: on a fully-enriched basket every per-item flag is fresh, so
 *      any `false` still blocks — this preserves compatibility with the
 *      pre-SSF-413 behaviour on the enriched path.
 *
 * The `basketAtpStatus` argument is kept in the signature (rather than
 * removed) for call-site self-documentation, and reserved for a future
 * "block only when ATP has spoken" mode if we ever need to distinguish
 * "PLI flag persisted false" from "PLI flag never populated".
 */
export function isCheckoutBlockedCartItem(
    item: Record<string, unknown> | null | undefined,
    basketAtpStatus?: AtpStatus | null
): boolean {
    return getCheckoutBlockReason(item, basketAtpStatus) !== null;
}

export function getCheckoutBlockReason(
    item: Record<string, unknown> | null | undefined,
    // Kept in the signature for call-site self-documentation and reserved
    // for future use; the current rule trusts the per-PLI flag regardless
    // of basket status (see docblock on `isCheckoutBlockedCartItem`).
    _basketAtpStatus?: AtpStatus | null
): 'hard_unavailable' | 'atp_unavailable' | 'price_unavailable' | null {
    if (!item) return null;
    // Checkout blocking is intentionally stricter than row-level inventory alerts:
    // low-stock copy (`showInventoryMessage`) should NOT block checkout.
    if (isHardUnavailableCartItem(item)) {
        return 'hard_unavailable';
    }
    // PLI-flag-first: an explicit `false` here can only come from a
    // successful ATP call for the current (zip, cart) state having
    // persisted the verdict onto `pli.custom.atpIsAvailable`.
    if (item.c_atpIsAvailable === false || item.c_atpIsAvailable === 'false') {
        return 'atp_unavailable';
    }
    // Pricing-availability block — stamped by
    // sfnext_ashley_hooks/CalculateBasket via PricingAvailabilityHelper on
    // every basket recalc. `false` means the shopper's active zone price
    // books have no coverage for this SKU (or the merchandise product no
    // longer resolves) — legitimate $0 lines (Extend warranty,
    // mattress-takeback `*RETURN`, non-catalog / option / bundled / bonus
    // PLIs) are stamped `true` or left undefined by design so this branch
    // never falses on them. Returned as a distinct reason so ops
    // analytics can split the two failure modes, but the shopper-facing
    // copy is the same as `atp_unavailable` by product decision (one
    // consistent "not available at your ZIP" message for both).
    if (item.c_priceIsAvailable === false || item.c_priceIsAvailable === 'false') {
        return 'price_unavailable';
    }
    return null;
}
